import { createContext, useContext, useState } from "react";

import { api } from "../../lib/api";

import {
  IPartnerDataResponse,
  IPartnerResponse,
  IPartnerDataProps,
  IPartnerProviderData,
} from "./types";

export const PartnerDataContext = createContext<IPartnerProviderData>(
  {} as IPartnerProviderData
);

/**
 * Fallback estático de personalização: quando o cadastro do parceiro não tem
 * cssStyle/imgLogo, tenta os arquivos em public/partners/<url>/.
 * O cadastro (API) sempre tem prioridade.
 */
const applyStaticBranding = async (data: IPartnerDataResponse, partnerUrl: string) => {
  const base = `${process.env.PUBLIC_URL}/partners/${partnerUrl.toLowerCase()}`;

  if (!data.cssStyle) {
    try {
      const res = await fetch(`${base}/custom.css`);
      const type = res.headers.get("content-type") || "";
      // dev server devolve index.html (text/html) para caminhos inexistentes
      if (res.ok && type.includes("css")) {
        data.cssStyle = await res.text();
      }
    } catch {
      // sem fallback estático: segue com o padrão
    }
  }

  if (!data.imgLogo) {
    try {
      const res = await fetch(`${base}/logo.png`);
      const type = res.headers.get("content-type") || "";
      if (res.ok && type.includes("image")) {
        data.imgLogo = `${base}/logo.png`;
      }
    } catch {
      // sem logo estático: Header usa a marca em texto
    }
  }

  return data;
};

export const PartnerDataProvider = ({ children }: IPartnerDataProps) => {
  const [partnerUrl, setpartnerUrl] = useState<string>();
  const [partnerData, setPartnerData] = useState<IPartnerDataResponse>();
  const [hasPartner, setHasPartner] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const loadPartner = async (partnerUrl: string) => {
    setpartnerUrl(partnerUrl);

    try {
      setIsLoading(true);

      const { data } = (await api.get(`/Partner/partnersByURL/${partnerUrl.toLowerCase()}`)) as IPartnerResponse;

      if (data) {
        await applyStaticBranding(data, partnerUrl);
        setHasPartner(true);
        setPartnerData(data);

        const manifest = {
          short_name: data.nome || "Conectadoc Partner",
          name: "Encontre o melhor profissional para atende-lo!",
          icons: [
            {
              src: `https://conectadoc.com.br/${data.url}/favicon.png`,
              sizes: "512x512 192x192 64x64 32x32 24x24 16x16",
              type: "image/png"
            }
          ],
          start_url: `https://conectadoc.com.br/${data.url}`,
          display: "standalone",
          theme_color: "#000000",
          background_color: "#ffffff"
        }
        
        // Criar blob URL para o manifest
        const blob = new Blob([JSON.stringify(manifest)], {
          type: 'application/json'
        });
        const manifestURL = URL.createObjectURL(blob);
        
        // Atualizar link do manifest
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = manifestURL;
        document.head.appendChild(link);
      }
    } catch (error) {
      setHasPartner(false);
      console.error(error);
    }

    setIsLoading(false);
  };

  return (
    <PartnerDataContext.Provider
      value={{
        partnerUrl,
        hasPartner,
        partnerData,
        loadPartner,
        isLoading,
        setIsLoading,
      }}
    >
      {children}
    </PartnerDataContext.Provider>
  );
};

export const usePartnerData = () => useContext(PartnerDataContext);
